import React from "react";

import VideoRecordingStartAction from "../../redux/actions/videoPD/VideoRecordingStartAction";
import { toast } from "react-toastify";
import { VIDEO_UPLOAD_START_ONLINE_RESET } from "../../redux/actions/types";
import { useDispatch, useSelector } from "react-redux";

export const useAnalyze = (
  videoRef, // Can be remoteRef (applicant) or localRef (CM)
  meetingId,
  frameIndexRef,
  remoteVideoActive,
  handleLogs,
  setResponses, // Optional - not needed for CM
  setHasAnalysisStarted, // Optional - not needed for CM
  video_pdn_id,
  userType = "Applicant", // "Applicant" or "CM"
) => {
  const [isAnalyzing, setIsAnalyzing] = React.useState(false);
  const analyzeIntervalRef = React.useRef(null);
  const dispatch = useDispatch();
  const videoRecordingStartReducer = useSelector(
    (state) => state.videoRecordingStartReducer,
  );
  const request_id = video_pdn_id() ? String(video_pdn_id()) : null;

  const isAnalyzingRef = React.useRef(isAnalyzing);
  const remoteVideoActiveRef = React.useRef(remoteVideoActive);

  React.useEffect(() => {
    isAnalyzingRef.current = isAnalyzing;
  }, [isAnalyzing]);

  React.useEffect(() => {
    remoteVideoActiveRef.current = remoteVideoActive;
  }, [remoteVideoActive]);

  /**
   * Captures a single frame from the video stream and sends it to the server for analysis.
   * It uses a canvas to convert the current video frame into a JPEG blob.
   */
  const sendFrameToAPI = React.useCallback(() => {
    if (videoRef.current && remoteVideoActiveRef.current) {
      const canvas = document.createElement("canvas");
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoRef.current, 0, 0);

      canvas.toBlob(async (blob) => {
        if (!blob) return;

        const loanDetailsStr = localStorage.getItem("loan_details");
        const details = loanDetailsStr ? JSON.parse(loanDetailsStr) : {};
        const loanID = details?.loan_id ? String(details?.loan_id) : null;

        const userTypeLabel =
          userType === "CM"
            ? "CM"
            : details?.user_type?.name ||
              details?.user_type_label ||
              "Applicant";
        const sessionVersion = details?.version
          ? Number(details.version) + 1
          : 1;

        const prefix = `${loanID}/${userTypeLabel}`;
        const filePrefix = prefix.replace(/\//g, "_");
        const fileName = `${filePrefix}_version_${sessionVersion}_${frameIndexRef.current}.jpg`;

        const formData = new FormData();

        formData.append(
          "live_streaming_image_file",
          blob,
          `frame_${frameIndexRef.current}.jpg`,
        );

        formData.append("loan_id", loanID);
        formData.append("user_type", userTypeLabel);
        formData.append("version", sessionVersion);
        formData.append("img_frame_name", fileName);

        // const imgPath = `Test/${prefix}/version_${sessionVersion}/${fileName}`;
        const imgPath = `production/${prefix}/version_${sessionVersion}/${fileName}`;
        formData.append("img_frame_path", imgPath);
        formData.append("is_online", 2);
        formData.append("meeting_id", meetingId);

        if (frameIndexRef.current === 0) {
          formData.append("request_id", request_id);
        }

        try {
          // Sending frame to API via Redux action
          await dispatch(VideoRecordingStartAction(formData));
          frameIndexRef.current += 1;
        } catch (err) {
          console.error("Frame upload failed", err);
        }
      }, "image/jpeg");
    }
  }, [videoRef, meetingId, dispatch, frameIndexRef, request_id, userType]);

  const handleAnalyzeToggle = React.useCallback(() => {
    if (!isAnalyzingRef.current) {
      if (setHasAnalysisStarted) {
        setHasAnalysisStarted(true);
      }
    }

    if (!remoteVideoActiveRef.current) {
      toast.warning(`Start a call first to analyze ${userType} video.`, {
        autoClose: 2000,
        toastId: `start_call_analyze_${userType}`,
      });
      return;
    }

    if (isAnalyzingRef.current) {
      handleLogs(`${userType} Analysis stopped`);
      clearInterval(analyzeIntervalRef.current);
      analyzeIntervalRef.current = null;
      setIsAnalyzing(false);
    } else {
      handleLogs(`${userType} Analysis started`);
      setIsAnalyzing(true);
      frameIndexRef.current = 0;
      analyzeIntervalRef.current = setInterval(sendFrameToAPI, 1000 / 10); // 1 FPS
    }
  }, [
    handleLogs,
    setIsAnalyzing,
    setHasAnalysisStarted,
    sendFrameToAPI,
    frameIndexRef,
    userType,
  ]);

  React.useEffect(() => {
    if (videoRecordingStartReducer?.data?.status_code === 2000) {
      const result = videoRecordingStartReducer?.data?.result;
      if (setResponses) {
        if (result && Object.keys(result).length > 0) {
          const score = result.status;
          if (score !== undefined && score !== null) {
            setResponses((prev) => [score, ...prev]);
          }
        } else {
          setResponses((prev) => ["No score found", ...prev]);
        }
      }
      dispatch({ type: VIDEO_UPLOAD_START_ONLINE_RESET });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRecordingStartReducer]);

  return { handleAnalyzeToggle, isAnalyzing };
};
